import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import AppSelect, { AppSelectController } from "../../components/Core/AppSelect/AppSelect";
import { FormCard } from "../../components/Core/FormCard";
import Button from "../../components/Core/Button/Button";
import CheckboxNew from "../../components/Core/Checkbox/CheckboxNew";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import { CustomerCreateBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { ViewModels } from "../../configs/constants/ViewModels";
import { routes } from "../../configs/constants/routes";
import Address from "../../models/Address";
import Contract from "../../models/Contract";
import CorporateGroup from "../../models/CorporateGroup";
import { CreateCustomerRequest, DeliveryMethod, InvoiceDetails } from "../../models/Customer";
import { CONTRACT_SORTING_COLUMN } from "../../models/Sorting";
import { useCreateCustomer } from "../../networking/customer.hooks";
import { useCustomFieldDefinitions, useOrganisation } from "../../networking/organisation.hooks";
import {
	CustomFieldFormValues,
	CustomFieldsSection,
	buildCustomFieldsPayload,
	customFieldDefaults,
	customFieldsSchema,
} from "../../components/Shared/CustomFields";
import {
	DeliveryMethodRadioGroup,
	getDeliveryErrorTarget,
	getDeliveryMethodOptions,
	getEffectiveDeliveryMethod,
	getGLNError,
	isEInvoice,
	usesDeliveryField,
} from "../../components/Shared/DeliveryMethod";
import { useSubsidiaries } from "../../networking/subsidiary.hooks";
import {
	useAvailablePools,
	ORGANISATION_DIRECTLY_VALUE,
} from "../../components/Shared/SubsidiarySelect/SubsidiarySelect";
import phoneNumberSchema from "../../schemas/phoneNumberSchema";
import postalCodeSchema from "../../schemas/postalCodeSchema";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { customerRoute } from "../../utils/route.utils";
import { Pool } from "../../models/Organisation";
import { useContracts } from "../../networking/contract.hooks";
import { useCorporateGroup, useCorporateGroups } from "../../networking/corporateGroup.hooks";
import CorporateGroupFormModal from "./CorporateGroupFormModal";
import CustomerTypeSelector, { CustomerProfileType } from "./CustomerTypeSelector";
import { toast } from "../../utils/toast";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

type CustomerInputs = {
	name: string;
	phoneNumber: string;
	vatNumber: string;
	active: boolean;
	invoiceDetails: InvoiceDetails;
	contract: string;
	description?: string;
	internalNotes?: string;
	visitingAddress?: Address;
	pool?: string;
	subsidiaryId?: string;
	customFields?: CustomFieldFormValues;
};

type SubsidiaryOption = { key: string; name: string };

const CustomerInputsValidationSchema = yup.object().shape({
	active: yup.boolean(),
	contract: yup.string().required("FIELD_IS_REQUIRED").uuid(),
	phoneNumber: phoneNumberSchema,
	vatNumber: yup.string(), //.required("FIELD_IS_REQUIRED"),
	name: yup.string().required("FIELD_IS_REQUIRED"),
	visitingAddress: yup.object().shape({
		postalCode: postalCodeSchema,
	}),
	invoiceDetails: yup.object().shape({
		postalCode: postalCodeSchema,
	}),
});

const CustomerCreatePage: React.FC = () => {
	const { data: organisation } = useOrganisation();
	const modalStore = useConfirmModalStore();
	const { mutateAsync: createCustomer } = useCreateCustomer();
	const { t } = useTranslation();
	const { contracts = [] } = useContracts({
		view: ViewModels.SELECT_OPTION,
		active: true,
		sort: CONTRACT_SORTING_COLUMN.NAME,
		isDeleted: false,
	});

	const [customerType, setCustomerType] = useState<CustomerProfileType>("COMPANY");
	const [corporateGroupId, setCorporateGroupId] = useState<string>();
	const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);

	const inCorporateGroup = customerType === "CORPORATE_GROUP";
	const exportType = organisation?.invoiceSettings?.exportType;
	const deliveryMethodOptions = getDeliveryMethodOptions({
		exportType,
		privatePerson: customerType === "PRIVATE",
	});
	const { corporateGroup } = useCorporateGroup(inCorporateGroup ? corporateGroupId : undefined);
	const { corporateGroups } = useCorporateGroups(
		{ page: 1, pageSize: 1000, sort: "name" },
		inCorporateGroup
	);

	const navigate = useNavigate();
	const nonArchivedContracts = contracts.filter((c) => !c.isDeleted);

	const { subsidiaries } = useSubsidiaries();

	// Org-defined custom fields (required-ness is form-only). All customer
	// fields are created here, rendered inside the card their placement names
	// (Grunduppgifter or Fakturamottagare, mirroring the two detail tabs).
	const customFieldDefinitions = useCustomFieldDefinitions("customer");
	// Once a koncern is picked its group-owned fields switch to read-only
	// (the koncern's values, fetched above): never required, never sent.
	const corporateGroupMember = inCorporateGroup && !!corporateGroupId;
	const corporateGroupValues = corporateGroup ? corporateGroup.customFields ?? {} : undefined;
	const validationSchema = useMemo(
		() =>
			CustomerInputsValidationSchema.shape({
				customFields: customFieldsSchema(customFieldDefinitions, undefined, {
					corporateGroupMember,
				}),
				// The data the chosen delivery method needs (mirrors org-service); a
				// koncern member takes its method from the group, so nothing to check.
				vatNumber: yup
					.string()
					.test("delivery-method", "deliveryMethod.field.vatNumber", function (value) {
						return (
							inCorporateGroup ||
							this.parent.invoiceDetails?.deliveryMethod !== "E_INVOICE_ORG" ||
							!!value?.trim()
						);
					}),
				invoiceDetails: yup.object().shape({
					postalCode: postalCodeSchema,
					// 13 digits when set, for every method; required for E_INVOICE_GLN
					GLNCode: yup
						.string()
						.nullable()
						.test("gln", "deliveryMethod.field.GLNCode", function (value) {
							if (inCorporateGroup) return true;

							const message = getGLNError(this.parent.deliveryMethod, value);

							return message ? this.createError({ message }) : true;
						}),
					email: yup
						.string()
						.nullable()
						.test("email", "deliveryMethod.field.email", function (value) {
							return inCorporateGroup || this.parent.deliveryMethod !== "EMAIL" || !!value?.trim();
						}),
				}),
			}),
		[customFieldDefinitions, corporateGroupMember, inCorporateGroup]
	);

	const { register, handleSubmit, control, formState, setValue, watch, setError, clearErrors } =
		useForm<CustomerInputs>({
			mode: "onBlur",
			resolver: yupResolver(validationSchema),
			defaultValues: {
				contract: "",
				active: true,
				invoiceDetails: { deliveryMethod: "PRINT" },
				customFields: {},
			},
		});

	const pickedDeliveryMethod = watch("invoiceDetails.deliveryMethod") ?? "PRINT";
	const invoiceEmail = watch("invoiceDetails.email");
	// Shown, not stored: the pick survives switching back from a koncern
	const deliveryMethod =
		inCorporateGroup && corporateGroup
			? getEffectiveDeliveryMethod(corporateGroup.invoiceDetails)
			: pickedDeliveryMethod;

	// Switching to private person drops e-invoice: fall back to email or print
	useEffect(() => {
		if (deliveryMethodOptions.includes(pickedDeliveryMethod)) return;

		setValue("invoiceDetails.deliveryMethod", invoiceEmail?.trim() ? "EMAIL" : "PRINT");
	}, [deliveryMethodOptions, pickedDeliveryMethod, invoiceEmail, setValue]);

	function onDeliveryMethodChange(value: DeliveryMethod) {
		setValue("invoiceDetails.deliveryMethod", value);
		// What is required just changed; submit validates it again
		clearErrors([
			"invoiceDetails.deliveryMethod",
			"invoiceDetails.GLNCode",
			"invoiceDetails.email",
			"vatNumber",
		]);
	}

	// Seed the custom fields with their definition defaults once the org has
	// loaded — once only, so a background refetch can't clobber edits.
	const customFieldsSeeded = useRef(false);
	useEffect(() => {
		if (customFieldsSeeded.current || customFieldDefinitions.length === 0) return;
		customFieldsSeeded.current = true;
		setValue("customFields", customFieldDefaults(customFieldDefinitions));
	}, [customFieldDefinitions, setValue]);

	useEffect(() => {
		// Set default value of contract to the configured default contract in organisation
		const defaultContract = nonArchivedContracts.find(
			(c) => c.id === organisation?.defaultContractId
		);
		if (defaultContract) {
			setValue("contract", defaultContract.id);
		}

		const defaultPool = organisation?.pools?.find((p) => p.isDefault);

		if (defaultPool) {
			setValue("pool", defaultPool.key);
		}
	}, [organisation, nonArchivedContracts, setValue]);

	const availablePools = useAvailablePools(
		control,
		"subsidiaryId",
		subsidiaries,
		organisation?.pools
	);

	// "Inget dotterbolag" uses a sentinel value (Radix forbids an empty
	// SelectItem value); on submit it maps to an undefined subsidiaryId.
	const subsidiaryOptions: SubsidiaryOption[] = [
		{ key: ORGANISATION_DIRECTLY_VALUE, name: t("NO_SUBSIDIARY") },
		...subsidiaries.map((s) => ({ key: s.id, name: s.name })),
	];

	useEffect(() => {
		// Mirror the selected corporate group's invoice details into the (locked)
		// invoice fields. Only `reference` stays editable for a group member.
		if (inCorporateGroup && corporateGroup) {
			setValue("invoiceDetails.invoiceName", corporateGroup.name);
			setValue("invoiceDetails.address1", corporateGroup.invoiceDetails?.address1);
			setValue("invoiceDetails.address2", corporateGroup.invoiceDetails?.address2);
			setValue("invoiceDetails.postalCode", corporateGroup.invoiceDetails?.postalCode);
			setValue("invoiceDetails.city", corporateGroup.invoiceDetails?.city);
			setValue("invoiceDetails.email", corporateGroup.invoiceDetails?.email);
			setValue("invoiceDetails.GLNCode", corporateGroup.invoiceDetails?.GLNCode);
		}
	}, [inCorporateGroup, corporateGroup, setValue]);

	const lockInvoiceFields = inCorporateGroup && !!corporateGroupId;

	const onSubmit = handleSubmit(async (e) => {
		if (inCorporateGroup && !corporateGroupId) {
			toast.error(t("corporateGroup.selectRequired"));
			return;
		}

		const customerData: CreateCustomerRequest = {
			name: e.name,
			phoneNumber: e.phoneNumber,
			vatNumber: e.vatNumber,
			active: e.active,
			contract: { id: e.contract },
			privatePerson: customerType === "PRIVATE" || undefined,
			corporateGroup: inCorporateGroup && corporateGroupId ? { id: corporateGroupId } : undefined,
			// Group-owned invoice details live on the corporate group; a member
			// customer only carries its own reference
			invoiceDetails: inCorporateGroup
				? { reference: e.invoiceDetails?.reference }
				: {
						invoiceName: e?.invoiceDetails?.invoiceName,
						address1: e.invoiceDetails?.address1,
						address2: e.invoiceDetails?.address2,
						postalCode: e.invoiceDetails?.postalCode,
						city: e.invoiceDetails?.city,
						reference: e.invoiceDetails?.reference,
						GLNCode: e.invoiceDetails?.GLNCode,
						email: e.invoiceDetails?.email,
						deliveryMethod: e.invoiceDetails?.deliveryMethod,
				  },
			visitingAddress: {
				address1: e.visitingAddress?.address1,
				postalCode: e.visitingAddress?.postalCode,
				city: e.visitingAddress?.city,
			},
			pool: e.pool || undefined,
			subsidiaryId:
				e.subsidiaryId && e.subsidiaryId !== ORGANISATION_DIRECTLY_VALUE
					? e.subsidiaryId
					: undefined,
			// Always the full map — undefined when the org has no customer fields.
			// A koncern member leaves out the group-owned keys (the koncern's values apply).
			customFields: buildCustomFieldsPayload(e.customFields, customFieldDefinitions, {
				corporateGroupMember,
			}),
		};

		// The hook invalidates the customer list caches and toasts on failure
		// (mutateAsync rejects, so the confirm modal is skipped). Also mark the
		// field org-service named.
		const result = await createCustomer(customerData).catch(({ error }) => {
			const target = getDeliveryErrorTarget(error);

			if (target) setError(`invoiceDetails.${target.field}`, { message: target.message });

			return undefined;
		});

		if (result) {
			const customerConfirmModal: ConfirmModalProps = {
				title: t("customer.created.title"),
				text: t(result.active ? "customer.created.infoActive" : "customer.created.infoInactive"),
				acceptButtonText: t("customer.created.yes"),
				rejectButtonText: t("customer.created.no"),
				onReject: () => navigateToCustomerList(),
				onAccept: () => navigateToCustomerProfile(result.id),
			};
			modalStore.newModal(customerConfirmModal);
		}
	});

	const navigateToCustomerList = () => {
		navigate(routes.CUSTOMERS);
	};

	const navigateToCustomerProfile = (id?: string) => {
		const routePath = customerRoute(id || "");
		navigate(routePath);
	};

	return (
		<div className="p-4 px-6">
			<Breadcrumbs items={CustomerCreateBreadcrumbs} />
			<form onSubmit={onSubmit}>
				<div className="mx-auto flex max-w-[720px] flex-col gap-5 py-6 pb-28">
					<FormCard title="BASIC_INFO">
						<div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
							<AppInputNew
								{...register("name")}
								label="customer.name"
								placeholder="customer.namePlaceholder"
								errors={formState.errors?.name?.message}
								mandatory
							/>
							{/* Input height, lifted by the margin of AppInputNew's always-rendered error line */}
							<div className="flex items-center md:mb-[0.35rem] md:h-[2.3125rem] md:self-end">
								<CheckboxNew {...register("active")} label="ACTIVATE" />
							</div>
							<AppInputNew
								{...register("phoneNumber")}
								label="PHONE"
								placeholder="PHONE_PLACEHOLDER"
								icon="phone"
								errors={formState.errors?.phoneNumber?.message}
							/>
							<AppInputNew
								{...register("vatNumber")}
								label="ORG_OR_PERSONAL_NUMBER"
								placeholder="ORG_OR_PERSONAL_NUMBER"
								errors={formState.errors?.vatNumber?.message}
								mandatory={!inCorporateGroup && deliveryMethod === "E_INVOICE_ORG"}
							/>
						</div>

						<div className="mt-4">
							<AppSelectController<Contract>
								control={control}
								label="CONTRACT"
								name="contract"
								options={nonArchivedContracts}
								getOptionLabel={(c) => c.name}
								getOptionValue={(c) => c.id}
								errors={formState.errors?.contract?.message}
								placeholder="SELECT_CONTRACT"
								margin="0"
								mandatory
							/>
						</div>

						{availablePools.length > 0 && (
							<div className="mt-4">
								<AppSelectController<Pool>
									control={control}
									label="POOL"
									name="pool"
									options={availablePools}
									getOptionLabel={(option) => option.name}
									getOptionValue={(o) => o.key as string}
									errors={formState.errors?.pool?.message}
									placeholder="SELECT_POOL"
									margin="0"
									mandatory
								/>
							</div>
						)}

						{subsidiaries.length > 0 && (
							<div className="mt-4">
								<AppSelectController<SubsidiaryOption>
									control={control}
									label="BELONGS_TO_SUBSIDIARY"
									name="subsidiaryId"
									options={subsidiaryOptions}
									getOptionLabel={(o) => o.name}
									getOptionValue={(o) => o.key}
									placeholder="NO_SUBSIDIARY"
									margin="0"
								/>
							</div>
						)}

						{/* Customer fields placed under Profil — hidden when there are none. */}
						<CustomFieldsSection
							entity="customer"
							placement="profil"
							variant="inline"
							definitions={customFieldDefinitions}
							control={control}
							register={register}
							errors={formState.errors}
							corporateGroupMember={corporateGroupMember}
							corporateGroupValues={corporateGroupValues}
						/>
					</FormCard>

					<FormCard title="VISITING_ADDRESS">
						<div className="flex flex-col gap-4">
							<AppInputNew
								{...register("visitingAddress.address1")}
								label="STREET_ADDRESS"
								placeholder={t("ADDRESS_PLACEHOLDER")}
							/>
							<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
								<AppInputNew
									{...register("visitingAddress.postalCode")}
									label="POSTAL_CODE"
									placeholder="POSTAL_CODE_PLACEHOLDER"
									errors={formState.errors?.visitingAddress?.postalCode?.message}
								/>
								<AppInputNew
									{...register("visitingAddress.city")}
									label="CITY"
									placeholder="CITY_PLACEHOLDER"
								/>
							</div>
						</div>
					</FormCard>

					<FormCard title="INVOICE_RECIPIENT">
						<div className="flex flex-col gap-4">
							<div>
								<p className="mb-1.5 text-sm font-medium text-muted-foreground">
									{t("CUSTOMER_TYPE")}
								</p>
								<CustomerTypeSelector value={customerType} onChange={setCustomerType} />

								{inCorporateGroup && (
									<div className="mt-4 mb-1.5 flex items-center gap-3">
										<div className="flex-1">
											<AppSelect<CorporateGroup>
												name="corporateGroup"
												options={corporateGroups?.corporateGroups || []}
												getOptionLabel={(group) => group.name}
												getOptionValue={(group) => group.id}
												value={corporateGroupId}
												onChange={(id) => setCorporateGroupId(id || undefined)}
												placeholder="corporateGroup.selectPlaceholder"
												margin="0"
											/>
										</div>
										<Button
											type="button"
											icon="addBlue"
											backgroundColor="buttonGray"
											textColor="dark"
											fontSize="s"
											height="2.3125rem"
											onClick={() => setShowCreateGroupModal(true)}
										>
											{t("corporateGroup.createNew")}
										</Button>
									</div>
								)}
							</div>

							<AppInputNew
								{...register("invoiceDetails.invoiceName")}
								label="customer.invoiceName"
								placeholder="INVOICE_NAME"
								disabled={lockInvoiceFields}
							/>
							<AppInputNew
								{...register("invoiceDetails.reference")}
								label={lockInvoiceFields ? "corporateGroup.referenceLabel" : "REFERENCE"}
								placeholder="REFERENCE"
							/>
						</div>

						{/* Customer fields placed under Fakturainformation — hidden when there are none. */}
						<CustomFieldsSection
							entity="customer"
							placement="faktura"
							variant="inline"
							definitions={customFieldDefinitions}
							control={control}
							register={register}
							errors={formState.errors}
							corporateGroupMember={corporateGroupMember}
							corporateGroupValues={corporateGroupValues}
						/>
					</FormCard>

					<FormCard title="INVOICE_ADDRESS">
						<div className="flex flex-col gap-4">
							<AppInputNew
								{...register("invoiceDetails.address1")}
								placeholder="INVOICE_ADDRESS_LINE_1_PLACEHOLDER"
								disabled={lockInvoiceFields}
							/>
							<AppInputNew
								{...register("invoiceDetails.address2")}
								placeholder="INVOICE_ADDRESS_LINE_2"
								disabled={lockInvoiceFields}
							/>
							<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
								<AppInputNew
									{...register("invoiceDetails.postalCode")}
									placeholder="POSTAL_CODE_PLACEHOLDER"
									errors={formState.errors?.invoiceDetails?.postalCode?.message}
									disabled={lockInvoiceFields}
								/>
								<AppInputNew
									{...register("invoiceDetails.city")}
									placeholder="CITY_PLACEHOLDER"
									disabled={lockInvoiceFields}
								/>
							</div>
						</div>
					</FormCard>

					<FormCard title="E_INVOICE">
						<div className="flex flex-col gap-4">
							<DeliveryMethodRadioGroup
								containerClassName="mb-2"
								value={deliveryMethod}
								onChange={onDeliveryMethodChange}
								options={deliveryMethodOptions}
								exportType={exportType}
								disabled={inCorporateGroup}
								note={
									inCorporateGroup
										? t("deliveryMethod.fromCorporateGroup")
										: exportType === "FORTNOX" && isEInvoice(deliveryMethod)
										? t("deliveryMethod.eInvoiceIntent")
										: undefined
								}
								errors={
									formState.errors.invoiceDetails?.deliveryMethod?.message &&
									t(formState.errors.invoiceDetails.deliveryMethod.message as any)
								}
							/>
							<AppInputNew
								{...register("invoiceDetails.email")}
								label="EMAIL_INVOICE"
								placeholder="EMAIL_INVOICE_PLACEHOLDER"
								disabled={
									inCorporateGroup || !usesDeliveryField(deliveryMethod, "email", { exportType })
								}
								mandatory={!inCorporateGroup && deliveryMethod === "EMAIL"}
								errors={formState.errors.invoiceDetails?.email?.message}
							/>
							<AppInputNew
								{...register("invoiceDetails.GLNCode")}
								label="customer.gln"
								placeholder="GLN_PLACEHOLDER"
								disabled={
									inCorporateGroup || !usesDeliveryField(deliveryMethod, "GLNCode", { exportType })
								}
								mandatory={!inCorporateGroup && deliveryMethod === "E_INVOICE_GLN"}
								errors={formState.errors.invoiceDetails?.GLNCode?.message}
							/>
						</div>
					</FormCard>

					{/* Customer fields placed under "Övriga faktureringsuppgifter" — own card, hidden when there are none. */}
					<CustomFieldsSection
						entity="customer"
						placement="faktura-ovrigt"
						title="Övriga faktureringsuppgifter"
						definitions={customFieldDefinitions}
						control={control}
						register={register}
						errors={formState.errors}
						corporateGroupMember={corporateGroupMember}
						corporateGroupValues={corporateGroupValues}
					/>

					{/* Customer fields placed under "Övriga uppgifter" — own card, hidden when there are none. */}
					<CustomFieldsSection
						entity="customer"
						placement="ovrigt"
						definitions={customFieldDefinitions}
						control={control}
						register={register}
						errors={formState.errors}
						corporateGroupMember={corporateGroupMember}
						corporateGroupValues={corporateGroupValues}
					/>
				</div>

				<FloatingButtonPanel>
					<Button width="9.75rem">{t("CREATE_CUSTOMER")}</Button>
				</FloatingButtonPanel>
			</form>

			{showCreateGroupModal && (
				<CorporateGroupFormModal
					onClose={() => setShowCreateGroupModal(false)}
					onSaved={(group) => setCorporateGroupId(group.id)}
				/>
			)}
		</div>
	);
};

export default CustomerCreatePage;
